import { useTranslation } from 'react-i18next'

interface DimensionLockButtonProps {
  locked: boolean
  disabled?: boolean
  onToggle: () => void
}

/**
 * Padlock toggle guarding the label width/height editors. When locked, both
 * ruler dimension inputs and the canvas resize drag handles are disabled so
 * the label size can't be changed by mistake. The state persists with the
 * document (`dimensionsLocked` on BlockDefinition / Template).
 */
export function DimensionLockButton({ locked, disabled, onToggle }: DimensionLockButtonProps) {
  const { t } = useTranslation()
  const title = t(locked ? 'canvas.labelSize.unlock' : 'canvas.labelSize.lock')
  return (
    <button
      type="button"
      data-testid="dimension-lock"
      aria-pressed={locked}
      aria-label={title}
      title={title}
      disabled={disabled}
      onClick={onToggle}
      className={[
        'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded border text-stone-500',
        locked
          ? 'border-amber-400 bg-amber-50 text-amber-600 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-400'
          : 'border-stone-300 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-400 dark:hover:bg-stone-800',
        'disabled:cursor-not-allowed disabled:opacity-50',
      ].join(' ')}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="4" y="11" width="16" height="10" rx="2" />
        {locked ? (
          // Closed shackle
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        ) : (
          // Open shackle (swung off to the right)
          <path d="M8 11V7a4 4 0 0 1 7.5-2" />
        )}
      </svg>
    </button>
  )
}
